import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SolutionPage } from "@/components/support/SolutionPage";
import { resolutionService } from "@/lib/support-docs/resolution-service";
import { SupportTicket, AIResolutionAttempt, SupportSolution } from "@/types/support";
import { Loader2, Bot } from 'lucide-react';

// Interface para campo do formulário
interface BaseField {
  id: string;
  label: string;
  placeholder?: string;
  type?: string;
  optional?: boolean;
}

interface SingleField extends BaseField {
  isGroup: false;
  value?: string;
  onChange: (value: string) => void;
}

interface GroupField extends BaseField {
  isGroup: true;
  fields: (BaseField & {
    value?: string | boolean;
    onChange: (value: string | boolean) => void;
  })[];
}

type Question = SingleField | GroupField;

interface FormFieldProps extends BaseField {
  value?: string | boolean;
  onChange: (value: string | boolean) => void;
}

// Schema de validação
const formSchema = z.object({
  main: z.string().min(1, "por favor, selecione uma categoria principal"),
  issue: z.string().min(1, "por favor, selecione um problema"),
  description: z.string().min(10, "a descrição deve ter pelo menos 10 caracteres"),
  email: z.string().email("e-mail inválido").min(1, "e-mail é obrigatório"),
  newsletter: z.string().min(1, "por favor, selecione uma newsletter"),
});

type FormValues = z.infer<typeof formSchema>;

// Categorias principais e suas subcategorias
const SUPPORT_CATEGORIES = {
  "Programa de indicação e prêmios": [
    "Não estou conseguindo indicar o the news",
    "Meu prêmio chegou com defeito",
    "Não recebi meus prêmios",
    "Giveaway"
  ],
  "Streak (pontuação diária)": [
    "Meus pontos no streak não estão sendo contabilizados"
  ],
  "Problemas de recebimento das edições e cadastro": [
    "Me desinscrevi e continuo recebendo",
    "Não estou recebendo a edição",
    "Alteração de cadastro"
  ],
  "Feedback e Erros": [],
  "Outros": [],
  "Quiz": []
} as const;

// Configurações das newsletters
const NEWSLETTER_CONFIG = {
  'thenews': {
    color: '#FECF04',
    logo: '/thenews.webp',
    title: 'suporte the news'
  },
  'thebizness': {
    color: '#FF66C4',
    logo: '/thebizness.webp',
    title: 'suporte the bizness'
  },
  'rising': {
    color: '#CFFCA4',
    logo: '/rising.webp',
    title: 'suporte rising'
  },
  'goget': {
    color: '#FBAC5F',
    logo: '/goget.webp',
    title: 'suporte goget'
  },
  'thechamps': {
    color: '#C72828',
    logo: '/thechamps.webp',
    title: 'suporte the champs'
  },
  'healthtimes': {
    color: '#579EDE',
    logo: '/healthtimes.webp',
    title: 'suporte health times'
  },
  'thejams': {
    color: '#FFCF03',
    logo: '/thejams.webp',
    title: 'suporte the jams'
  },
  'pod-isso-vic': {
    color: '#F2A9DF',
    logo: '/podisso.webp',
    title: 'suporte pod isso, vic?'
  },
  'thejobs': {
    color: '#000000',
    logo: '/thejobs.webp',
    title: 'suporte the jobs'
  },
  'thestories': {
    color: '#FF66C4',
    logo: '/thestories.webp',
    title: 'suporte the stories'
  },
  'waffle': {
    color: '#FA2586',
    logo: '/logo.png',
    title: 'suporte waffle'
  }
} as const;

export function SupportForm() {
  const [searchParams] = useSearchParams();
  const newsletterParam = searchParams.get('newsletter');
  const newsletter = newsletterParam && NEWSLETTER_CONFIG[newsletterParam as keyof typeof NEWSLETTER_CONFIG] 
    ? newsletterParam 
    : 'waffle';
  const config = NEWSLETTER_CONFIG[newsletter as keyof typeof NEWSLETTER_CONFIG] || NEWSLETTER_CONFIG['waffle'];
  
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showInitialPage, setShowInitialPage] = useState(true);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Estados para o sistema de resolução inteligente
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [showSolutionPage, setShowSolutionPage] = useState(false);
  const [currentAttempt, setCurrentAttempt] = useState<AIResolutionAttempt | null>(null);
  const [currentSolution, setCurrentSolution] = useState<SupportSolution | null>(null);
  const [skipAISolution, setSkipAISolution] = useState(false);
  
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      main: "",
      issue: "",
      description: "",
      email: searchParams.get('email') || "",
      newsletter: newsletter,
    },
    mode: "onSubmit"
  });

  // Verificar se é the news ou waffle para mostrar opções específicas
  const isTheNewsOrWaffle = newsletter === 'thenews' || newsletter === 'waffle';
  
  // Filtrar categorias baseado na newsletter
  const getFilteredCategories = () => {
    return Object.entries(SUPPORT_CATEGORIES).filter(([category]) => {
      if (isTheNewsOrWaffle) return true;
      return !category.includes('Streak') && 
             !category.includes('prêmio') && 
             !category.includes('indicação');
    }).reduce((acc, [category, issues]) => {
      acc[category] = issues;
      return acc;
    }, {} as Record<string, readonly string[]>);
  };

  const filteredCategories = getFilteredCategories();

  // Array de questões
  const formQuestions: Question[] = [
    { 
      id: 'category_group',
      label: 'Selecione a categoria e o problema',
      isGroup: true,
      fields: [
        {
          id: 'main',
          label: 'Categoria Principal',
          type: 'radio',
          value: '',
          onChange: () => {}
        },
        {
          id: 'issue',
          label: 'Problema Específico',
          type: 'radio',
          value: '',
          onChange: () => {}
        }
      ]
    },
    { 
      id: 'description', 
      label: 'Conte-nos melhor sobre o seu problema', 
      placeholder: 'Por favor, escreva sua resposta aqui',
      isGroup: false,
      type: 'textarea',
      value: '',
      onChange: () => {}
    },
    { 
      id: 'email', 
      label: 'E-mail', 
      placeholder: 'seu@email.com', 
      isGroup: false,
      type: 'email',
      value: '',
      onChange: () => {}
    }
  ];

  // Função para iniciar o formulário
  const startForm = () => {
    setShowInitialPage(false);
    setCurrentQuestionIndex(0);
  };

  // Calcula progresso do formulário
  useEffect(() => {
    if (currentQuestionIndex === -1) {
      setProgress(0);
    } else {
      const progress = ((currentQuestionIndex + 1) / formQuestions.length) * 100;
      setProgress(progress);
    }
  }, [currentQuestionIndex, formQuestions.length]);

  const analyzeAndOfferSolution = async (data: FormValues): Promise<boolean> => {
    setIsAnalyzing(true);
    
    try {
      // Cria o ticket temporário para análise
      const ticket: SupportTicket = {
        main: data.main,
        issue: data.issue,
        description: data.description,
        email: data.email,
        newsletter: data.newsletter,
        wantReply: true
      };
      
      // Analisa o ticket com o serviço de resolução
      const attempt = await resolutionService.analyzeTicket(ticket);
      setCurrentAttempt(attempt);
      
      // Verifica se deve oferecer solução automatizada
      if (resolutionService.shouldOfferAutomatedSolution(attempt)) {
        const solution = resolutionService.findSolutionForProblem(attempt.problemMatched!);
        if (solution) {
          setCurrentSolution(solution);
          setShowSolutionPage(true);
          return true; // Indica que uma solução foi oferecida
        }
      }
      
      return false; // Nenhuma solução encontrada
    } catch (error) {
      console.error('Erro ao analisar ticket:', error);
      return false;
    } finally {
      setIsAnalyzing(false);
    }
  };

  const sendTicketToSupport = async (data: FormValues) => {
    try {
      setLoading(true);
      setError('');

      // Prepara os dados para envio
      const formData = {
        main: data.main,
        issue: data.issue,
        description: data.description,
        email: data.email,
        newsletter: data.newsletter,
        wantReply: true,
        aiAttempted: currentAttempt ? true : false,
        aiSolutionOffered: currentSolution ? true : false,
        aiSolutionAccepted: false // Foi rejeitada se chegou aqui
      };
      
      const response = await fetch('https://backend.testeswaffle.org/webhooks/support', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Erro ao enviar formulário' }));
        throw new Error(errorData.message || 'Erro ao enviar formulário');
      }

      setIsSuccess(true);
      form.reset();
      
      setTimeout(() => {
        setIsSuccess(false);
        setShowInitialPage(true);
        setCurrentQuestionIndex(-1);
        setCurrentAttempt(null);
        setCurrentSolution(null);
        setSkipAISolution(false);
      }, 3000);

    } catch (error) {
      console.error('Erro:', error);
      setError(error instanceof Error ? error.message : 'Ocorreu um erro ao enviar o formulário. Por favor, tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: FormValues) => {
    // Se o usuário optou por pular a solução IA ou se não é aplicável
    if (skipAISolution || 
        ['Feedback e Erros', 'Outros', 'Quiz'].includes(data.main)) {
      await sendTicketToSupport(data);
      return;
    }
    
    // Tenta encontrar uma solução automatizada primeiro
    const solutionOffered = await analyzeAndOfferSolution(data);
    
    // Se não encontrou solução, envia direto para suporte
    if (!solutionOffered) {
      await sendTicketToSupport(data);
    }
  };

  const handleCreateTicketAfterSolution = () => {
    setShowSolutionPage(false);
    setSkipAISolution(true);
    const data = form.getValues();
    sendTicketToSupport(data);
  };

  const handleBackFromSolution = () => {
    setShowSolutionPage(false);
    setIsSuccess(true);
    form.reset();
    setTimeout(() => {
      setIsSuccess(false);
      setShowInitialPage(true);
      setCurrentQuestionIndex(-1);
      setCurrentAttempt(null);
      setCurrentSolution(null);
      setSkipAISolution(false);
    }, 2000);
  };

  const handleChangeSolution = (solutionId: string) => {
    const newSolution = resolutionService.solutions.find(s => s.id === solutionId);
    if (newSolution) {
      setCurrentSolution(newSolution);
      // Criar uma nova tentativa para a solução relacionada
      const newAttempt: AIResolutionAttempt = {
        ticketId: currentAttempt?.ticketId || 'temp-' + Date.now(),
        problemMatched: newSolution.problemId,
        solutionOffered: newSolution.id,
        confidenceScore: 0.8, // Confiança alta para soluções relacionadas
        timestamp: new Date()
      };
      setCurrentAttempt(newAttempt);
    }
  };

  const handleNextQuestion = () => {
    const currentQuestion = formQuestions[currentQuestionIndex];
    let isValid = true;

    if (currentQuestion.isGroup) {
      // Não há mais grupo, então não faz nada
    } else {
      const value = form.getValues(currentQuestion.id as any);
      if (!value && !currentQuestion.optional) {
        isValid = false;
        form.setError(currentQuestion.id as any, { message: 'Campo obrigatório' });
      }
      // Validação específica para o campo de descrição
      if (currentQuestion.id === 'description' && value && value.length < 10) {
        isValid = false;
        form.setError('description', { message: 'A descrição deve ter pelo menos 10 caracteres' });
      }
      // Validação para e-mail
      if (currentQuestion.id === 'email' && value && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value)) {
        isValid = false;
        form.setError('email', { message: 'E-mail inválido' });
      }

      // Redirecionar para o CSAT se for the news e a opção "Outro" for selecionada
      if (currentQuestion.id === 'issue' && value === 'Outro' && newsletter === 'thenews') {
        window.location.href = 'https://tally.so/r/w2o7GL';
        return;
      }
    }

    if (isValid && currentQuestionIndex < formQuestions.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
    }
  };

  const getCurrentQuestion = () => formQuestions[currentQuestionIndex];
  
  const renderField = (field: FormFieldProps) => {
    if (field.type === 'radio' && field.id === 'main') {
      return (
        <div className="space-y-4">
          <Select
            value={field.value as string}
            onValueChange={(value: string) => {
              field.onChange(value);
              // Limpa o valor do issue quando a categoria principal muda
              form.setValue('issue', '');
            }}
          >
            <SelectTrigger className="w-full bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 transition-all h-14 text-base shadow-sm hover:border-gray-300 focus:ring-2 focus:ring-opacity-20"
                         style={{ 
                           '--tw-ring-color': config.color,
                           'borderColor': 'var(--tw-ring-color)' 
                         } as React.CSSProperties}>
              <SelectValue placeholder="Qual categoria você precisa de ajuda?" />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-200 rounded-xl shadow-lg">
              {Object.keys(filteredCategories).map((category) => (
                <SelectItem
                  key={category}
                  value={category}
                  className="text-base py-3 px-4 cursor-pointer transition-colors hover:bg-gray-50"
                >
                  {category}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    
    if (field.type === 'radio' && field.id === 'issue') {
      const mainCategory = form.getValues('main');
      const issues = filteredCategories[mainCategory as keyof typeof filteredCategories] || [];

      // Se a categoria principal for uma das que devem redirecionar para o CSAT
      if (['Feedback e Erros', 'Outros', 'Quiz'].includes(mainCategory)) {
        window.location.href = 'https://tally.so/r/w2o7GL';
        return null;
      }

      return (
        <div className="space-y-4">
          <Select
            value={field.value as string}
            onValueChange={(value: string) => field.onChange(value)}
            disabled={!mainCategory}
          >
            <SelectTrigger className="w-full bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 transition-all h-14 text-base shadow-sm hover:border-gray-300 focus:ring-2 focus:ring-opacity-20"
                         style={{ 
                           '--tw-ring-color': config.color,
                           'borderColor': 'var(--tw-ring-color)' 
                         } as React.CSSProperties}>
              <SelectValue placeholder={mainCategory ? "Selecione o problema que está enfrentando" : "Selecione primeiro uma categoria"} />
            </SelectTrigger>
            <SelectContent className="bg-white border border-gray-200 rounded-xl shadow-lg">
              {issues.map((issue) => (
                <SelectItem
                  key={issue}
                  value={issue}
                  className="text-base py-3 px-4 cursor-pointer transition-colors hover:bg-gray-50"
                >
                  {issue}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      );
    }
    
    if (field.type === 'checkbox') {
      return null;
    }
    
    if (field.type === 'textarea') {
      return (
        <div className="space-y-2">
          <textarea
            placeholder={field.placeholder}
            value={field.value as string}
            onChange={(e) => field.onChange(e.target.value)}
            className="w-full min-h-[150px] p-4 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 resize-none transition-all shadow-sm hover:border-gray-300 focus:ring-2 focus:ring-opacity-20"
            style={{ 
              '--tw-ring-color': config.color,
              'borderColor': 'var(--tw-ring-color)' 
            } as React.CSSProperties}
          />
          <div className="text-sm text-gray-500 text-right">
            {(field.value as string).length}/10 caracteres mínimos
          </div>
        </div>
      );
    }
    
    return (
      <Input
        type={field.type || 'text'}
        placeholder={field.placeholder}
        value={field.value as string}
        onChange={(e) => field.onChange(e.target.value)}
        className="bg-white border border-gray-200 rounded-xl text-gray-900 placeholder-gray-500 transition-all shadow-sm hover:border-gray-300 h-14 focus:ring-2 focus:ring-opacity-20"
        style={{ 
          '--tw-ring-color': config.color,
          'borderColor': 'var(--tw-ring-color)' 
        } as React.CSSProperties}
      />
    );
  };

  if (loading || isAnalyzing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin mx-auto mb-4" style={{ color: config.color }} />
          <p className="text-lg text-gray-600">
            {isAnalyzing ? (
              <>
                <Bot className="inline-block w-5 h-5 mr-2" />
                Analisando seu problema...
              </>
            ) : (
              'Enviando para o suporte...'
            )}
          </p>
        </div>
      </div>
    );
  }

  // Se estiver mostrando a página de solução
  if (showSolutionPage) {
    return (
      <SolutionPage
        solution={currentSolution}
        attempt={currentAttempt}
        ticket={form.getValues()}
        newsletterColor={config.color}
        onBack={handleBackFromSolution}
        onCreateTicket={handleCreateTicketAfterSolution}
        config={config}
        onChangeSolution={handleChangeSolution}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-8 relative" style={{ '--newsletter-color': config.color } as React.CSSProperties}>
      <div className="w-full max-w-5xl p-6">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex-1 flex flex-col justify-center text-center"
        >
          <img 
            src={config.logo}
            alt={config.title}
            className="w-48 mx-auto mb-8"
          />
          
          <AnimatePresence mode="wait">
            {showInitialPage ? (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="space-y-8"
              >
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-6"
                >
                  <h1 className="text-4xl font-bold text-gray-900">
                    {config.title}
                  </h1>
                  <p className="text-gray-600 text-lg max-w-xl mx-auto">
                    como podemos te ajudar hoje?
                  </p>
                  <Button
                    onClick={startForm}
                    className="w-full max-w-md mx-auto text-white py-6 text-lg rounded-xl transition-all hover:opacity-90"
                    style={{ backgroundColor: config.color }}
                  >
                    começar →
                  </Button>
                </motion.div>
              </motion.div>
            ) : isSuccess ? (
              <motion.div 
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                className="bg-white rounded-3xl p-12 shadow-sm border border-gray-100 max-w-3xl mx-auto text-center"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", duration: 0.5 }}
                  className="flex items-center justify-center w-20 h-20 rounded-full text-white mx-auto mb-6"
                  style={{ backgroundColor: config.color }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </motion.div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">resposta enviada com sucesso! 🎉</h2>
                <p className="text-xl text-gray-700 font-['Inter']">
                  entraremos em contato em breve através do seu e-mail!
                </p>
              </motion.div>
            ) : (
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  <motion.div
                    key={currentQuestionIndex}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.5 }}
                    className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-gray-100 max-w-3xl mx-auto"
                  >
                    {getCurrentQuestion().isGroup ? (
                      <div className="space-y-6">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                          {getCurrentQuestion().label}
                        </h2>
                        <div className="space-y-6">
                          {(getCurrentQuestion() as GroupField).fields.map((field) => (
                            <FormField
                              key={field.id}
                              control={form.control}
                              name={field.id as any}
                              render={({ field: fieldProps }) => (
                                <FormItem>
                                  <FormControl>
                                    {renderField({ ...field, ...fieldProps })}
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          ))}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-6">
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
                          {getCurrentQuestion().label}
                        </h2>
                        <FormField
                          control={form.control}
                          name={getCurrentQuestion().id as any}
                          render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                {renderField({ ...getCurrentQuestion() as SingleField, ...field })}
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    )}

                    <div className="mt-8 flex justify-between items-center">
                      <button
                        type="button"
                        onClick={handlePreviousQuestion}
                        disabled={currentQuestionIndex === 0}
                        className="text-base md:text-lg text-gray-500 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        ← anterior
                      </button>
                      {currentQuestionIndex === formQuestions.length - 1 ? (
                        <Button
                          type="submit"
                          className="text-white px-8 py-6 text-lg rounded-xl transition-all hover:opacity-90"
                          style={{ backgroundColor: config.color }}
                          disabled={loading}
                        >
                          {loading ? (
                            <div className="w-6 h-6 border-4 border-white border-t-transparent rounded-full animate-spin" />
                          ) : (
                            'enviar'
                          )}
                        </Button>
                      ) : (
                        <Button
                          type="button"
                          onClick={handleNextQuestion}
                          className="text-white px-8 py-6 text-lg rounded-xl transition-all hover:opacity-90"
                          style={{ backgroundColor: config.color }}
                        >
                          próximo →
                        </Button>
                      )}
                    </div>
                  </motion.div>

                  <div className="mt-auto flex justify-center items-center space-x-4">
                    <div className="h-2 w-48 bg-gray-200 rounded-full overflow-hidden">
                      <motion.div 
                        className="h-full"
                        style={{ backgroundColor: config.color }}
                        initial={{ width: "0%" }}
                        animate={{ width: `${progress}%` }}
                        transition={{ duration: 0.3 }}
                      />
                    </div>
                    <span className="text-base md:text-lg text-gray-500">
                      {currentQuestionIndex + 1} de {formQuestions.length}
                    </span>
                  </div>
                
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.4 }}
                      className="bg-gray-50 rounded-xl p-4 border border-gray-200 text-gray-700 text-center max-w-2xl mx-auto"
                    >
                      <p className="text-sm">
                        {newsletter === 'thenews' ? (
                          "Se você quer comentar sobre a edição ou sinalizar algum erro, use o espaço logo após a pergunta 'Na sua visão, a edição de hoje foi imparcial?'. É lá que você pode escrever sugestões ou recados pro nosso time de redação!"
                        ) : (
                          "Quer deixar um comentário sobre a edição ou apontar algum erro? Use o espaço no final da edição, na seção de opinião dos leitores. É lá que você pode enviar sugestões, recados ou observações para o nosso time de redação."
                        )}
                      </p>
                    </motion.div>
            
                  {error && (
                    <motion.p
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="text-red-500 text-center mt-2"
                    >
                      {error}
                    </motion.p>
                  )}
                </form>
              </Form>
            )}
          </AnimatePresence>
        </motion.div>

        {/* Barra de Progresso Fixa */}
        <div className="absolute bottom-0 left-0 w-full h-2 bg-gray-200">
          <motion.div 
            className="h-full"
            style={{ backgroundColor: config.color }}
            initial={{ width: "0%" }}
            animate={{ width: `${progress}%` }}
            transition={{ duration: 0.3 }}
          />
        </div>
      </div>
    </div>
  );
} 